import * as React from 'react';
import { Res, SmartMenuPaymentStatusDTO, Permission, SmartMenuOrderSettingsType, SmartMenuOrderCommStatus } from 'core/api';
import * as UI from 'core/ui';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as Images from 'pos/Images';
import { SmartMenuFacade } from 'pos/facades';

import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import TableOrderSaleCenterCommConfigurationScreen from './TableOrderSaleCenterCommConfigurationScreen';
import { Flex } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

type Props = {
	status: SmartMenuPaymentStatusDTO,
} & Modal.Actions<void>;

class ConfigureSmartMenuOrderCommsScreen extends PosScreenClassComponent<Props, SmartMenuPaymentStatusDTO> {

	createInitialState(props: Props): Nullable<SmartMenuPaymentStatusDTO> {
		return { ...props.status };
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getScreenId(): string {
		return 'CONFIGURE_SMART_MENU_ORDER_COMMS';
	}

	protected getTitle(): string {
		return Res.Web.CONFIGURE_SMART_MENU_ORDER_COMMS();
	}

	protected getImage(): string | SvgIcon {
		return Images.ConfigureSmartMenuOrderComms;
	}

	protected useKeypad() {
		return false;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			null
		];
	}

	protected getContent(): React.ReactNode {

		const takeOutActionButton = (type: SmartMenuOrderSettingsType.Delivery | SmartMenuOrderSettingsType.TakeAway, status: SmartMenuOrderCommStatus) => {

			const onSuspend = async (type: SmartMenuOrderSettingsType) => {
				await this.execute({
					action: async () => {
						const status = type === SmartMenuOrderSettingsType.Delivery ? await SmartMenuFacade.suspendDeliveryComms() : await SmartMenuFacade.suspendTakeAwayComms();
						this.setState(status);
					}
				});
			};

			const onResume = async (type: SmartMenuOrderSettingsType) => {
				await this.execute({
					action: async () => {
						const status = type === SmartMenuOrderSettingsType.Delivery ? await SmartMenuFacade.resumeDeliveryComms() : await SmartMenuFacade.resumeTakeAwayComms();
						this.setState(status);
					}
				});
			};

			const iconClass = status === SmartMenuOrderCommStatus.Suspended
				? 'fa fa-play'
				: 'fa fa-pause';

			const onClick = status === SmartMenuOrderCommStatus.Suspended
				? async () => await onResume(type)
				: async () => await onSuspend(type);

			const text = status === SmartMenuOrderCommStatus.Suspended
				? Res.Web.CONFIGURE_SMART_MENU_ORDER_COMMS_RESUME_BTN()
				: Res.Web.CONFIGURE_SMART_MENU_ORDER_COMMS_SUSPEND_BTN();

			return <a className='link' onClick={onClick} title={text}>
				<small><i className={iconClass + ' m-r-xs'}></i> {text}</small>
			</a>;
		};

		const tableOrderActionButton = () => {

			const onConfigure = async () => {
				const result = await Modal.show(TableOrderSaleCenterCommConfigurationScreen, { actualStatus: this.state.TableOrderSaleCenterStatus });
				if (!result.ok)
					return;

				const status = await SmartMenuFacade.suspendOrResumeTableOrder(result.value);
				this.setState(status);
			};

			const onClick = async () => await onConfigure();
			const text = Res.Web.CONFIGURE_SMART_MENU_ORDER_COMMS_CONFIGURE_BTN();

			return <a className='link' onClick={onClick} title={text}>
				<small><i className={'fa fa-cog m-r-xs'}></i> {text}</small>
			</a>;
		};

		// TODO: Seguramente sería bueno sacar esto a un componente genérico porque
		// se parece mucho al card de la fidelización
		const block = (image: string | SvgIcon, type: SmartMenuOrderSettingsType.Delivery | SmartMenuOrderSettingsType.TakeAway | SmartMenuOrderSettingsType.TableOrder, status: SmartMenuOrderCommStatus) => {

			const Icon = image;
			const disabled = status === SmartMenuOrderCommStatus.Disabled;
			const style: React.CSSProperties | undefined = status !== SmartMenuOrderCommStatus.Enabled ? {
				filter: 'grayscale(1)',
				opacity: 0.6
			} : undefined;

			const actionButton = type === SmartMenuOrderSettingsType.TableOrder ? tableOrderActionButton() : takeOutActionButton(type, status);
			return <div className='text-center text-18 m-t-xxxl'>
				<div style={style}>
					{UI.isString(image) ? <img src={image} width={48} height={48} style={{ display: 'inline-block' }} /> : <Icon width={48} height={48} style={{ display: 'inline-block' }} />}
					<div className='m-t-sm'>{SmartMenuOrderSettingsType.toString(type)}</div>
					<div ><small>{SmartMenuOrderCommStatus.toString(status)}</small></div>
				</div>
				{disabled ? null : <div className='m-t-xs'>{actionButton}</div>}
			</div>;
		};

		return <Flex.Vertical>
			<Flex.Item>
				<Flex.Horizontal>
					<Flex.Item size='*' >{block(Images.Delivery, SmartMenuOrderSettingsType.Delivery, this.state.DeliveryStatus)}</Flex.Item>
					<Flex.Item size='*'>{block(Images.TakeAway, SmartMenuOrderSettingsType.TakeAway, this.state.TakeAwayStatus)}</Flex.Item>
				</Flex.Horizontal>
			</Flex.Item>
			<Flex.Item>
				<Flex.Horizontal>
					<Flex.Item size='*'>{block(Images.SelectSaleLocation, SmartMenuOrderSettingsType.TableOrder, this.state.TableOrderStatus)}</Flex.Item>
				</Flex.Horizontal>
			</Flex.Item>
		</Flex.Vertical>;
	}

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }
}

declare global {
	interface CommandType {
		configureSmartMenuOrderComms: () => Promise<Result>
	}
}

Command.register('configureSmartMenuOrderComms', Preconditions.and(Preconditions.ensure(Permission.SmartMenuOrderComms_Configure), Preconditions.ensureIsNotLite), async (): Promise<Result> => {
	const status = await SmartMenuFacade.getPaymentStatus();
	return Modal.show(ConfigureSmartMenuOrderCommsScreen, { status });
});
